import React from "react";
import { motion } from "framer-motion";
import { MapPin, Mail, Phone } from "lucide-react";
import {
    FaLinkedinIn,
    FaXTwitter,
    FaInstagram,
    FaYoutube,
    FaFacebookF,
    FaTelegram
} from "react-icons/fa6";

const Footer = () => {
    const currentYear = new Date().getFullYear();

    const socials = [
        { icon: FaFacebookF, link: "#" },
        { icon: FaInstagram, link: "https://www.instagram.com/fortune_signals?igsh=NGQ1OTZqcTRqYXMz" },
        { icon:FaTelegram, link: "https://t.me/FortuneSignals369"},
        { icon: FaXTwitter, link: "#" },
        { icon: FaLinkedinIn, link: "https://www.linkedin.com/in/fortune-signals-fzco-32675239a" },
        { icon: FaYoutube, link: "https://www.youtube.com/@FortuneSignalsFZCO" },
    ];

    return (
        <footer className="relative w-full bg-bg-dark-1 text-light overflow-hidden font-poppins">
            {/* Top Decorative Line */}
            <div className="absolute top-0 left-0 w-full h-[1px] bg-gradient-to-r from-transparent via-primary/50 to-transparent" />

            <div className="w-full pt-16 pb-12 px-5 sm:px-8 md:px-12 lg:px-20 xl:px-24">

                {/*
                    UPDATED GRID SYSTEM (12 Columns on XL):
                    XS (Mobile): grid-cols-1
                    SM/MD/LG (Tablet/Laptop): grid-cols-2 (Preserves the iPad fix)
                    XL (Desktop): grid-cols-12 (Allows granular width control)
                */}
                <div className="max-w-[1600px] mx-auto grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-12 gap-x-8 gap-y-12 xl:gap-8">

                    {/* --- BRANDING SECTION --- */}
                    {/*
                        XL: col-span-3 (25% width) - Reduced slightly to give space to Contact
                        SM/MD: col-span-2 (Full width)
                    */}
                    <div className="col-span-1 sm:col-span-2 xl:col-span-3 space-y-6">
                        <motion.div
                            initial={{ opacity: 0, y: 10 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.5 }}
                        >
                            <img
                                src="/assets/logo/FS_Dark.webp"
                                alt="Fortune Signals Logo"
                                className="w-32 sm:w-36 md:w-40 h-auto object-contain brightness-0 invert"
                            />
                        </motion.div>

                        <p className="text-light/60 text-sm leading-7 max-w-md xl:max-w-xs">
                            Empowering traders with time-based mentorship that adapts to all market conditions. Simplify your trading journey with Fortune Signals.
                        </p>

                        <div className="flex flex-wrap items-center gap-3">
                            {socials.map((item, i) => (
                                <motion.a
                                    key={i}
                                    href={item.link}
                                    whileHover={{ y: -3, backgroundColor: "#00619F", borderColor: "#00619F" }}
                                    className="
                                        group flex items-center justify-center
                                        w-10 h-10 rounded-full
                                        border border-light/10 bg-white/5
                                        text-light/70 hover:text-white
                                        transition-all duration-300
                                    "
                                    target="_blank"
                                    rel="noreferrer"
                                >
                                    <item.icon size={16} />
                                </motion.a>
                            ))}
                        </div>
                    </div>

                    {/* --- NAVIGATION LINKS --- */}
                    {/*
                        XL: col-span-2 (16.6% width each) - 3 columns total = 50% of width
                    */}

                    <FooterColumn
                        title="Company"
                        links={[
                            { label: "About Us", url: "/#about" },
                            { label: "Our Services", url: "/#services" },
                            { label: "Meet the Team", url: "/#team" },
                            { label: "Book a Session", url: "/courses/#webinar" },
                        ]}
                    />

                    <FooterColumn
                        title="Legal"
                        links={[
                            { label: "Risk Disclaimer", url: "/policies/risk-disclaimer" },
                            { label: "Terms & Conditions", url: "/policies/terms-and-conditions" },
                            { label: "Privacy Policy", url: "/policies/privacy-policy" },
                            { label: "Refund Policy", url: "/policies/refund-policy" },
                        ]}
                    />

                    <FooterColumn
                        title="Helpful Links"
                        links={[
                            { label: "FAQ", url: "/#faq" },
                            { label: "Book", url: "/#book" },
                            { label: "Contact Us", url: "#contact" },
                        ]}
                    />

                    {/* --- CONTACT SECTION --- */}
                    {/*
                        XL: col-span-3 (25% width) - WIDER COLUMN to fit the phone number
                        SM/MD: col-span-1 (Half width)
                    */}
                    <div className="col-span-1 xl:col-span-3">
                        <FooterContact />
                    </div>

                </div>
            </div>

            {/* --- BOTTOM BAR --- */}
            <div className="w-full bg-bg-dark-2 border-t border-light/5">
                <div className="max-w-[1600px] mx-auto px-5 sm:px-8 md:px-12 lg:px-20 xl:px-24 py-6 flex flex-col md:flex-row justify-between items-center gap-4 text-xs sm:text-sm text-light/40">
                    <div className="text-center md:text-left order-2 md:order-1 w-full md:w-auto">
                        <p>© {currentYear} Fortune Signals. All Rights Reserved.</p>
                    </div>
                    <div className="flex flex-wrap justify-center md:justify-end items-center gap-4 sm:gap-6 order-1 md:order-2 w-full md:w-auto">
                        <a href="/policies/terms-and-conditions" className="hover:text-primary transition-colors">Terms</a>
                        <span className="hidden sm:inline opacity-30">|</span>
                        <p className="text-center">
                            Designed by <span className="font-semibold text-primary/80 whitespace-nowrap">FORTUNE NOVEM</span>
                        </p>
                    </div>
                </div>
            </div>
        </footer>
    );
};

export default Footer;

/* ------------------------------------------------------------ */
/*                       SUB COMPONENTS                         */
/* ------------------------------------------------------------ */

function FooterContact() {
    return (
        <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="space-y-5"
        >
            <h4 className="text-lg font-semibold text-white tracking-wide">Contact Us</h4>

            <div className="space-y-4 text-light/70">
                <ContactItem
                    icon={MapPin}
                    text="Fortune Signals FZCO Level 41, Emirates Towers Sheikh Zayed Road Dubai UAE"
                    href="https://maps.app.goo.gl/WVVEoJkwMsSkh4L78"
                />
                <ContactItem
                    icon={Mail}
                    text="info@fortunesignals.ae"
                    href="mailto:info@fortunesignals.ae"
                />

                <ContactItem
                    icon={Phone}
                    text={
                        <>
                            <span className="text-white font-medium">BHAGESH GIRI : </span>{" "}
                            <span className="whitespace-nowrap">+971 50 369 9864</span>
                        </>
                    }
                    href="tel:+971503699864"
                />

                <ContactItem
                    icon={Phone}
                    text={
                        <>
                            <span className="text-white font-medium">VINAYAK JOSHI : </span>{" "}
                            <span className="whitespace-nowrap">+971 50 257 6323</span>
                        </>
                    }
                    href="tel:+971502576323"
                />
            </div>
        </motion.div>
    );
}

function FooterColumn({ title, links }) {
    return (
        <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ amount: 0.2, once: true }}
            // XL: col-span-2 ensures each link column takes 2/12ths (16.6%) of the width
            className="space-y-5 col-span-1 xl:col-span-2"
        >
            <h4 className="text-lg font-semibold text-white tracking-wide">{title}</h4>
            <ul className="space-y-3">
                {links.map((link, i) => (
                    <li key={i}>
                        <a
                            href={link.url}
                            className="
                                group flex items-center gap-2
                                text-sm text-light/60 hover:text-primary
                                transition-colors duration-300 w-fit
                            "
                        >
                            <span className="w-0 h-[1px] bg-primary transition-all duration-300 group-hover:w-3 opacity-0 group-hover:opacity-100" />
                            <span className="transform transition-transform duration-300 group-hover:translate-x-1">
                                {link.label}
                            </span>
                        </a>
                    </li>
                ))}
            </ul>
        </motion.div>
    );
}

function ContactItem({ icon: Icon, text, href }) {
    return (
        <a
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-start gap-3 group w-full"
        >
            <div className="
                flex-shrink-0 w-8 h-8 rounded-lg
                bg-white/5 border border-light/10
                flex items-center justify-center
                text-primary group-hover:bg-primary group-hover:text-white group-hover:border-primary
                transition-all duration-300 mt-[-2px]
            ">
                <Icon size={14} />
            </div>
            <span className="text-sm text-light/60 group-hover:text-light transition-colors leading-relaxed break-words min-w-0 flex-1">
                {text}
            </span>
        </a>
    );
}